import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Eye, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatData, openPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/dashboard/PageHeader";
import { ReportStatusBadge } from "@/components/dashboard/StatusBadge";

const FILTERS = [["in_verifica", "Da verificare"], ["approvata", "Verificate"], ["respinta", "Respinte"]];

export function AdminPanel() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("in_verifica");
  const [reports, setReports] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/reports", { params: { status: filter } });
      setReports(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [filter]);
  useEffect(() => { if (user.role === "admin") load(); }, [load, user.role]);

  if (user.role !== "admin") return <Navigate to="/app" replace />;

  const decide = async (id, status) => {
    const note = status === "respinta" ? window.prompt("Motivo del rifiuto (facoltativo)") : null;
    try {
      await api.post(`/admin/reports/${id}/status`, { status, note });
      toast.success(status === "approvata" ? "Segnalazione verificata e pubblicata" : "Segnalazione respinta");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Area amministratore" title="Verifica segnalazioni" text="Controlla il PDF del provvedimento prima di rendere la segnalazione consultabile nelle ricerche." />
      <div className="mb-6 flex gap-1 rounded-xl bg-slate-100 p-1 sm:inline-flex">
        {FILTERS.map(([k, label]) => (
          <button key={k} data-testid={`admin-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-lg px-4 py-2 text-sm font-semibold ${filter === k ? "bg-white text-[#1e3a8a] shadow-sm" : "text-slate-500"}`}>{label}</button>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {reports.length === 0 && <p data-testid="admin-reports-empty" className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500 lg:col-span-2">Nessuna segnalazione in questa categoria.</p>}
        {reports.map((r) => (
          <article key={r.id} data-testid={`admin-report-${r.id}`} className="surface p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-lg font-semibold text-[#0f172a]">{r.nome} {r.cognome}</p>
                <p className="text-xs text-slate-500">Nato/a il {formatData(r.data_nascita)}{r.codice_fiscale ? ` · CF ${r.codice_fiscale}` : ""}</p>
              </div>
              <ReportStatusBadge status={r.status} testid={`admin-report-status-${r.id}`} />
            </div>
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div><dt className="text-xs text-slate-500">Tribunale</dt><dd className="font-medium">{r.tribunale}</dd></div>
              <div><dt className="text-xs text-slate-500">R.G.</dt><dd className="font-mono text-[13px]">{r.numero_rg}</dd></div>
              <div><dt className="text-xs text-slate-500">Convalida</dt><dd>{formatData(r.data_convalida)}</dd></div>
              <div><dt className="text-xs text-slate-500">Segnalato da</dt><dd className="truncate">{r.owner_email}</dd></div>
            </dl>
            <div className="mt-5 flex flex-wrap gap-2">
              <button data-testid={`admin-view-pdf-${r.id}`} onClick={() => openPdf(r.id).catch((e) => toast.error(formatApiError(e)))} className="btn-ghost"><Eye className="h-4 w-4" />Apri PDF</button>
              {r.status !== "approvata" && <button data-testid={`admin-approve-${r.id}`} onClick={() => decide(r.id, "approvata")} className="btn-ghost hover:!border-[#16a34a] hover:!text-[#16a34a]"><Check className="h-4 w-4" />Approva</button>}
              {r.status !== "respinta" && <button data-testid={`admin-reject-${r.id}`} onClick={() => decide(r.id, "respinta")} className="btn-ghost hover:!border-[#dc2626] hover:!text-[#dc2626]"><X className="h-4 w-4" />Respingi</button>}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
